import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { getEdgePlay } from './EdgeSection';
import { useAuth } from '../context/AuthContext';
const HeaderStats = ({ metadata, games, sport }) => {
    const { user } = useAuth();
    const [userPerformance, setUserPerformance] = useState(null);
    const [isLoadingPerformance, setIsLoadingPerformance] = useState(true);
    // Fetch user performance data
    useEffect(() => {
        const fetchUserPerformance = async () => {
            if (!user) {
                setIsLoadingPerformance(false);
                return;
            }
            try {
                const token = localStorage.getItem('sharpshot_auth_token');
                const response = await fetch('/api/bets/performance?timeframe=season', {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    setUserPerformance(data.data);
                }
            }
            catch (error) {
                console.error('Error fetching user performance:', error);
            }
            finally {
                setIsLoadingPerformance(false);
            }
        };
        fetchUserPerformance();
    }, [user]);
    // Early return if metadata is not available
    if (!metadata) {
        return (_jsx("div", { className: "card mb-4", style: { backgroundColor: 'var(--card-bg)', borderColor: 'var(--card-border)' }, children: _jsx("div", { className: "card-body", children: _jsx("div", { className: "d-flex justify-content-center", children: _jsx("div", { className: "spinner-border", role: "status", children: _jsx("span", { className: "visually-hidden", children: "Loading..." }) }) }) }) }));
    }
    // Calculate current streak from recent bets
    const calculateCurrentStreak = (recentBets) => {
        if (!recentBets || recentBets.length === 0)
            return { streak: 0, isWinStreak: true };
        let streak = 0;
        const firstBetStatus = recentBets[0].status;
        // Only count settled bets for streak calculation
        const settledBets = recentBets.filter(bet => bet.status === 'won' || bet.status === 'lost');
        if (settledBets.length === 0)
            return { streak: 0, isWinStreak: true };
        const currentStatus = settledBets[0].status;
        for (const bet of settledBets) {
            if (bet.status === currentStatus) {
                streak++;
            }
            else {
                break;
            }
        }
        return { streak, isWinStreak: currentStatus === 'won' };
    };
    // Calculate stats combining real user data with today's potential
    const getStatsData = () => {
        // Count today's available games by confidence level
        const confidenceCounts = {
            HIGH: 0,
            'MEDIUM-PLUS': 0,
            MEDIUM: 0,
            LOW: 0
        };
        games.forEach(game => {
            const level = game.confidence_level;
            if (level in confidenceCounts) {
                confidenceCounts[level]++;
            }
        });
        // Edge-ranked sports (CFB/NFL) count edge plays as the value bets
        const edgeRanked = games.some(game => game.edge_context);
        const edgePlays = games.filter(game => getEdgePlay(game.edge_context));
        const corePlays = edgePlays.filter(game => getEdgePlay(game.edge_context)?.core).length;
        const highConfGames = edgeRanked
            ? edgePlays.length
            : confidenceCounts.HIGH + confidenceCounts['MEDIUM-PLUS'];
        // Calculate Today's Potential earnings
        const userBankroll = user?.bankroll || 1000;
        const betSizePercentage = 0.05; // 5% of bankroll per bet
        const betSize = userBankroll * betSizePercentage;
        const averageROI = 0.08; // 8% average ROI for high confidence bets
        const todaysPotential = highConfGames * betSize * averageROI;
        // Use real user performance data if available
        let userStats = {
            totalBets: 0,
            winRate: 0,
            currentStreak: 0,
            isWinStreak: true,
            totalProfitLoss: 0,
            unitsWonLost: 0,
            roi: 0
        };
        if (userPerformance && !isLoadingPerformance) {
            const streakData = calculateCurrentStreak(userPerformance.recentBets);
            const unitSize = userBankroll * 0.01;
            userStats = {
                totalBets: userPerformance.overall.totalBets || 0,
                winRate: isNaN(userPerformance.overall.winRate) ? 0 : userPerformance.overall.winRate,
                currentStreak: streakData.streak,
                isWinStreak: streakData.isWinStreak,
                totalProfitLoss: userPerformance.overall.totalProfit || 0,
                unitsWonLost: isNaN(userPerformance.overall.totalProfit / unitSize) ? 0 : userPerformance.overall.totalProfit / unitSize,
                roi: isNaN(userPerformance.overall.roi) ? 0 : userPerformance.overall.roi
            };
        }
        return {
            // Today's available games
            highConfGames,
            edgeRanked,
            corePlays,
            mediumGames: confidenceCounts.MEDIUM,
            lowGames: confidenceCounts.LOW,
            // Today's potential
            todaysPotential,
            // Real user performance metrics
            ...userStats
        };
    };
    const stats = getStatsData();
    return (_jsx("div", { className: "card mb-4", style: { backgroundColor: 'var(--card-bg)', borderColor: 'var(--card-border)' }, children: _jsx("div", { className: "card-body", children: _jsxs("div", { className: "row", children: [_jsxs("div", { className: "col-md-8", children: [_jsxs("h2", { className: "card-title mb-3", children: [_jsx("i", { className: "fas fa-chart-line text-primary me-2" }), "Week's ", sport?.shortName || 'CFB', " Predictions"] }), _jsx("p", { className: "card-text", children: stats.edgeRanked
                                    ? _jsxs(_Fragment, { children: [metadata.total_games, " games analyzed. ", _jsxs("strong", { children: [stats.highConfGames, " edge plays"] }), " on the board", stats.corePlays > 0 ? _jsxs(_Fragment, { children: [", including ", _jsxs("strong", { children: [stats.corePlays, " core plays"] })] }) : null, "."] })
                                    : _jsxs(_Fragment, { children: [metadata.total_games, " games analyzed with ", stats.highConfGames, " high-confidence picks, ", stats.mediumGames, " medium-confidence picks, and ", stats.lowGames, " low-confidence selections."] }) })] }), _jsx("div", { className: "col-md-4", children: _jsxs("div", { className: "row g-3", children: [_jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "h3 mb-0 text-primary", children: isLoadingPerformance ? (_jsx("i", { className: "fas fa-spinner fa-spin" })) : (stats.totalBets) }), _jsx("small", { className: "text-muted", children: "Total Bets" })] }) }), _jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center", children: [_jsxs("div", { className: "h3 mb-0 text-success", children: [(stats.winRate || 0).toFixed(1), "%"] }), _jsx("small", { className: "text-muted", children: "Win Rate" })] }) }), _jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: `h5 mb-0 ${stats.isWinStreak ? 'text-success' : 'text-danger'}`, children: isLoadingPerformance ? (_jsx("i", { className: "fas fa-spinner fa-spin" })) : stats.currentStreak > 0 ? (`${stats.isWinStreak ? 'W' : 'L'}${stats.currentStreak}`) : ('-') }), _jsx("small", { className: "text-muted", children: "Current Streak" })] }) }), _jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center", children: [_jsxs("div", { className: `h5 mb-0 ${stats.totalProfitLoss >= 0 ? 'text-success' : 'text-danger'}`, children: [stats.totalProfitLoss >= 0 ? '+' : '', "$", Math.round(stats.totalProfitLoss)] }), _jsx("small", { className: "text-muted", children: "Total P/L" })] }) }), _jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center", children: [_jsxs("div", { className: `h5 mb-0 ${stats.unitsWonLost >= 0 ? 'text-success' : 'text-danger'}`, children: [stats.unitsWonLost >= 0 ? '+' : '', stats.unitsWonLost.toFixed(1), "U"] }), _jsx("small", { className: "text-muted", children: "Units Won/Lost" })] }) }), _jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "h5 mb-0 text-info", children: stats.highConfGames }), _jsx("small", { className: "text-muted", children: stats.edgeRanked ? 'Edge Plays' : 'High Value Bets' })] }) }), _jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center", children: [_jsxs("div", { className: "h5 mb-0 text-success", children: ["+", (stats.roi || 0).toFixed(1), "%"] }), _jsx("small", { className: "text-muted", children: "Expected ROI" })] }) }), _jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center", children: [_jsxs("div", { className: "h5 mb-0 text-primary", children: ["$", Math.round(stats.todaysPotential)] }), _jsx("small", { className: "text-muted", children: "Max Earnings" })] }) })] }) })] }) }) }));
};
export default HeaderStats;
